/**
 * Shared design system style tokens.
 *
 * Provides reusable Tailwind class-based token objects for components that
 * share identical styling patterns (e.g., Card/Panel surfaces, Alert/Toast
 * feedback variants, Text colors, Icon colors, Avatar status indicators).
 *
 * All values reference semantic Tailwind token classes (e.g., `bg-secondary-100`,
 * `text-primary-700`) which resolve to CSS custom properties, enabling
 * ThemeOverride to restyle components at runtime.
 */

// ---------------------------------------------------------------------------
// Surface variant classes (Card / Panel shared pattern)
// ---------------------------------------------------------------------------

/**
 * Shared surface variant classes for Card and Panel.
 * Both components use an identical set of variant styles:
 *   default  - bg + border + shadow-sm
 *   elevated - bg + shadow-md
 *   outlined - bg + border only
 *   ghost    - transparent
 */
export const surfaceVariantClasses = {
  default: 'bg-secondary-100 border border-secondary-200 shadow-sm',
  elevated: 'bg-secondary-100 shadow-md',
  outlined: 'bg-secondary-100 border border-secondary-200',
  ghost: 'bg-transparent',
} as const;

// ---------------------------------------------------------------------------
// Feedback variant styles (Alert / Toast shared pattern)
// ---------------------------------------------------------------------------

/**
 * Shared feedback color sets used by Alert and Toast.
 * Each variant specifies background-subtle, text, icon class,
 * and border-left class.
 */
export const feedbackVariantColors = {
  success: {
    bg: 'bg-success-50',
    text: 'text-success-800',
    icon: 'text-success-700',
    border: 'border-l-success-700',
  },
  warning: {
    bg: 'bg-warning-50',
    text: 'text-warning-800',
    icon: 'text-warning-700',
    border: 'border-l-warning-700',
  },
  error: {
    bg: 'bg-error-50',
    text: 'text-error-800',
    icon: 'text-error-700',
    border: 'border-l-error-700',
  },
  info: {
    bg: 'bg-info-50',
    text: 'text-info-800',
    icon: 'text-info-700',
    border: 'border-l-info-700',
  },
} as const;

// ---------------------------------------------------------------------------
// Icon color map
// ---------------------------------------------------------------------------

/**
 * Color mapping for the Icon component. Exported so that other
 * components can reference the same semantic icon palette.
 */
export const iconColorMap = {
  inherit: 'currentColor',
  primary: 'text-primary-700',
  secondary: 'text-secondary-600',
  muted: 'text-secondary-500',
  error: 'text-error-700',
  success: 'text-success-700',
  warning: 'text-warning-700',
} as const;

// ---------------------------------------------------------------------------
// Text color map
// ---------------------------------------------------------------------------

/**
 * Color classes for the Text component, centralised for reuse.
 */
export const textColorClasses = {
  default: 'text-secondary-800',
  primary: 'text-primary-700',
  secondary: 'text-secondary-600',
  muted: 'text-secondary-500',
  error: 'text-error-700',
  success: 'text-success-700',
} as const;

// ---------------------------------------------------------------------------
// Status colors (Avatar)
// ---------------------------------------------------------------------------

export const statusColors = {
  online: 'bg-success-700',
  offline: 'bg-secondary-400',
  busy: 'bg-error-700',
  away: 'bg-warning-700',
} as const;
